import React from 'react';
import {Typography, Divider, Row, Col, Table} from 'antd';
import {formatAmount} from "@/components/common/utils/money";
import moment from 'moment';
import styled from 'styled-components';
import './deduction-form-print.css';
import {AppapproDTO, CompanyDTO} from '@/shared/api/model';
import {useDeductionFormData} from '@/hooks/loans/useDeductionFormData';
import StampComponent from './StampComponent';
import {DeductionFormConfig, DEFAULT_TEXT_CONTENT, DEFAULT_STAMP_CONFIG} from './DeductionFormConfig';

const {Title, Text} = Typography;

const resolveBodyText = (bodyText: string, productName: string) => {
    const trimmedProductName = productName.trim();

    if (!trimmedProductName) {
        return bodyText.replace(/\{productName\}/g, '').replace(/\s{2,}/g, ' ').trim();
    }

    if (bodyText.includes('{productName}')) {
        return bodyText.replace(/\{productName\}/g, trimmedProductName);
    }

    if (/makato ya\s+AKIBA/i.test(bodyText)) {
        return bodyText.replace(/makato ya\s+AKIBA/gi, `makato ya Mkopo wa ${trimmedProductName}`);
    }

    if (/makato ya\s+MKOPO\b/i.test(bodyText)) {
        return bodyText.replace(/makato ya\s+MKOPO\b/gi, `makato ya Mkopo wa ${trimmedProductName}`);
    }

    return bodyText;
};

// Styled components for the form
const FormContainer = styled.div`
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    background-color: white;
    font-family: Arial, sans-serif;
    line-height: 1.5;
    position: relative;
    overflow: hidden;
    id: "deduction-form-printable";
`;

const WatermarkLayer = styled.div<{ $url?: string }>`
    position: absolute;
    inset: 0;
    background-image: ${({$url}) => $url ? `url('${$url}')` : 'none'};
    background-repeat: no-repeat;
    background-position: center;
    background-size: 70%;
    opacity: 0.06;
    pointer-events: none;
    z-index: 0;
`;

const FormContent = styled.div`
    position: relative;
    z-index: 1;
`;

const FormHeader = styled.div`
    text-align: center;
    margin-bottom: 20px;
`;

const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 10px;
`;

const Logo = styled.img`
    height: 60px;
    width: auto;
`;

const FormTitle = styled(Title)`
    margin-top: 20px !important;
    text-align: center;
    text-decoration: underline;
    font-weight: bold !important;
`;

const SectionHeader = styled.div`
    background-color: #f0f0f0;
    padding: 8px 15px;
    margin: 15px 0;
    font-weight: bold;
    text-align: center;
`;

const FormField = styled.div`
    margin-bottom: 8px;
    display: flex;
`;

const FieldLabel = styled(Text)`
    font-weight: bold;
    min-width: 150px;
`;

const FieldValue = styled(Text)`
    flex: 1;
`;

const SignatureSection = styled.div`
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
`;

const SignatureField = styled.div`
    width: 45%;
`;

const SignatureLine = styled.div`
    border-bottom: 1px solid #000;
    margin-bottom: 5px;
    min-height: 30px;
    width: 100%;
    class: "signature-line";
`;

const DateField = styled.div`
    margin-top: 10px;
`;

const StampBox = styled.div`
    border: 1px solid #000;
    width: 150px;
    height: 150px;
    margin: 20px auto;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    class: "stamp-box";
`;

const ConsentBox = styled.div`
    border: 2px solid #000;
    padding: 15px;
    margin-top: 30px;
    background-color: #f9f9f9;
`;

export interface OfficerDetails {
    officerName?: string;
    officerInitials?: string;
    officerPhone?: string;
}

export interface FeeBreakdownData {
    disbursementFees?: Array<{ feeName?: string; calculatedAmount?: number }>;
    repaymentFees?: Array<{ feeName?: string; calculatedAmount?: number }>;
    totalDisbursementFees?: number;
    totalRepaymentFees?: number;
    netDisbursementAmount?: number;
    hasDisbursementFees?: boolean;
    hasRepaymentFees?: boolean;
}

interface WebDeductionFormComponentProps {
    user?: any;
    company?: CompanyDTO;
    product?: any;
    valueData?: AppapproDTO;
    scheduleData?: any;
    deductionFormConfig?: DeductionFormConfig;
    officerDetails?: OfficerDetails;
    feeBreakdown?: FeeBreakdownData;
    /** Number of decimal places for installment rounding (from product config) */
    installmentRoundingPrecision?: number | null;
    /** C-CODE value set by the officer (overrides branch) */
    cCode?: string;
}

const WebDeductionFormComponent: React.FC<WebDeductionFormComponentProps> = ({
                                                                                 user,
                                                                                 company,
                                                                                 product,
                                                                                 valueData,
                                                                                 scheduleData,
                                                                                 deductionFormConfig,
                                                                                 officerDetails,
                                                                                 feeBreakdown,
                                                                                 installmentRoundingPrecision,
                                                                                 cCode,
                                                                             }) => {
    const text = {...DEFAULT_TEXT_CONTENT, ...deductionFormConfig?.textContent};
    const officerName = officerDetails?.officerName || company?.contactPerson || '';
    const officerInitials = officerDetails?.officerInitials || '';
    const officerPhone = officerDetails?.officerPhone || company?.mobile || '';
    const resolvedProductName = product?.productName || valueData?.productName || '';
    const resolvedBodyText = resolveBodyText(text.bodyText, resolvedProductName);

    // Resolve header: config takes priority, fallback to company data
    const configHeader = deductionFormConfig?.header;
    const resolvedCompanyName = configHeader?.companyName || company?.brFname || company?.brName || '';
    const resolvedCompanyAddress = configHeader?.companyAddress || company?.address || '';
    const resolvedCompanyPhone = configHeader?.companyPhone || (company?.telephone ? `${company.telephone}${company?.mobile ? ` or ${company.mobile}` : ''}` : company?.mobile || '');
    const resolvedCompanyEmail = configHeader?.companyEmail || company?.email || '';
    const resolvedCompanyWebsite = configHeader?.companyWebsite || company?.website || '';
    const resolvedRegNo = company?.regNo || '';

    // Resolve branding: config takes priority, fallback to company data
    const configLogoUrl = deductionFormConfig?.branding?.logoUrl;
    const configWatermarkUrl = deductionFormConfig?.branding?.watermarkUrl;
    const showWatermark = deductionFormConfig?.branding?.showWatermark ?? false;
    const logoUrl = configLogoUrl || company?.logo || '';
    const watermarkUrl = showWatermark ? (configWatermarkUrl || logoUrl) : '';

    // Stamp: use image if stampImageUrl is set, otherwise SVG component
    const stampImageUrl = deductionFormConfig?.stamp?.stampImageUrl;

    // Resolve rounding precision: prop takes priority, then product config, then default (2)
    const resolvedRoundingPrecision = installmentRoundingPrecision ?? (product as any)?.installmentRoundingPrecision ?? null;

    // Use the data preparation hook
    const {
        loanAmount,
        loanPeriod,
        interestRate,
        installmentAmount,
        totalLoanAmount,
        maturityDate,
        disbursementDate,
        firstInstallmentDate
    } = useDeductionFormData(valueData, scheduleData, resolvedRoundingPrecision, feeBreakdown);

    // Format the current date
    const currentDate = moment().format('DD/MM/YYYY');
    const formattedDisbursementDate = disbursementDate ? moment(disbursementDate).format('DD/MM/YYYY') : currentDate;
    const formattedMaturityDate = maturityDate ? moment(maturityDate).format('DD/MM/YYYY') : 'N/A';
    const deductionMonthLabel = firstInstallmentDate ? moment(firstInstallmentDate).format('MMM/YYYY') : moment().format('MMM/YYYY');

    // Format loan amount, installment, and total loan amount (balance on deduction form)
    const formattedAmount = formatAmount(loanAmount);
    const formattedInstallment = formatAmount(installmentAmount);
    // @ts-ignore
    const formattedTotalLoanAmount = totalLoanAmount > 0 ? formatAmount(totalLoanAmount) : formattedAmount;

    // Table columns and data
    const columns = [
        {
            title: 'S/N',
            dataIndex: 'sn',
            key: 'sn',
            width: '10%',
        },
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
        },
        {
            title: 'CHECK NO',
            dataIndex: 'checkNo',
            key: 'checkNo',
            width: '15%',
        },
        {
            title: 'C-CODE',
            dataIndex: 'cCode',
            key: 'cCode',
            width: '15%',
        },
        {
            title: 'ONN',
            dataIndex: 'onn',
            key: 'onn',
            width: '15%',
        },
        {
            title: 'BALANCE',
            dataIndex: 'balance',
            key: 'balance',
            width: '15%',
        },
    ];

    const data = [
        {
            key: '1',
            sn: '1',
            name: valueData?.name || user?.fullName || 'MEMBER NAME',
            checkNo: valueData?.checkNumber || valueData?.empId || '',
            cCode: cCode || valueData?.cCode || '',
            onn: formattedInstallment,
            balance: formattedTotalLoanAmount,
        },
    ];

    return (
        <FormContainer id="deduction-form-printable">
            <WatermarkLayer aria-hidden $url={watermarkUrl}/>
            <FormContent>
                {/* Header with logo and company info */}
                <FormHeader>
                    <LogoContainer>
                        {logoUrl ? <Logo src={logoUrl} alt="Company Logo"/> : null}
                    </LogoContainer>
                    {resolvedCompanyName && (
                        <Title level={3} style={{margin: '0 0 4px 0', fontWeight: 'bold', textAlign: 'center'}}>
                            {resolvedCompanyName}
                        </Title>
                    )}
                    {resolvedCompanyAddress && (
                        <Text style={{display: 'block', textAlign: 'center'}}>{resolvedCompanyAddress}</Text>
                    )}
                    {resolvedCompanyPhone && (
                        <Text style={{display: 'block', textAlign: 'center'}}>
                            Tel: {resolvedCompanyPhone}{resolvedRegNo ? `, REG. No.DSR: ${resolvedRegNo}` : ''}
                        </Text>
                    )}
                    {(resolvedCompanyEmail || resolvedCompanyWebsite) && (
                        <div style={{textAlign: 'center', marginBottom: '4px'}}>
                            {resolvedCompanyEmail && <>Email: <a
                                href={`mailto:${resolvedCompanyEmail}`}>{resolvedCompanyEmail}</a></>}
                            {resolvedCompanyEmail && resolvedCompanyWebsite && ', '}
                            {resolvedCompanyWebsite && <>Website: <a
                                href={resolvedCompanyWebsite}>{resolvedCompanyWebsite}</a></>}
                        </div>
                    )}

                    <Divider style={{margin: '10px 0', borderColor: 'black', borderWidth: '2px'}}/>
                </FormHeader>

                {/* Reference Number and Date */}
                <Row justify="space-between" style={{marginBottom: '20px'}}>
                    <Col>
                        <Text strong>KUMB.NA.{text.referencePrefix}/{moment().format('MM/YYYY')}</Text>
                    </Col>
                    <Col>
                        <Text strong>{moment().format('DD/MM/YYYY')}</Text>
                    </Col>
                </Row>

                {/* Address Section */}
                <div style={{marginBottom: '20px'}}>
                    {(valueData?.employerAddress || '')
                        .split(/\r?\n/)
                        .map((l) => l.trim())
                        .filter(Boolean)
                        .map((line, idx) => (
                            <div key={idx}>
                                <Text strong>{line}</Text>
                            </div>
                        ))}
                    {!(valueData?.employerAddress || '').trim() && (
                        <Text strong>Anwani ya Mwajiri : </Text>
                    )}
                </div>

                {/* Subject Line */}
                <div style={{marginBottom: '20px', textAlign: 'center'}}>
                    <Text
                        strong>YAH: {text.subjectLine} - {valueData?.name?.toUpperCase() || valueData?.name?.toUpperCase() || 'MWANACHAMA'} VOTE {valueData?.voteNumber || ''}</Text>
                </div>

                {/* Main Content */}
                <div style={{marginBottom: '20px'}}>
                    <Text>
                        Rejea kichwa cha habari hapo juu.
                        <br/>
                        Tunawasilisha Jina la Mwanachama
                        wa {company?.brFname || company?.brName || ''} {resolvedBodyText} kwa
                        mwezi {deductionMonthLabel} na kuendelea.
                        <br/>
                        Pamoja na barua hii jina la mwanachama huyu limeambatanishwa kwa machanganuo Zaidi.
                    </Text>
                </div>

                {/* Loan Table */}
                <div style={{marginBottom: '20px'}}>
                    <div style={{textAlign: 'center', marginBottom: '10px'}}>
                        <Text strong>{text.tableSectionTitle}</Text>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        bordered
                        size="small"
                        style={{marginBottom: '10px'}}
                    />
                </div>

                {/* Signature Section */}
                <Row gutter={48}>
                    <Col span={12}>
                        <Text>{text.senderThankYouText}</Text>
                        <div style={{marginTop: '20px'}}>
                            <SignatureLine/>
                            <Text strong>{text.signerTitle1}</Text>
                            <div style={{marginTop: '10px'}}>
                                <Text strong>{text.signerTitle2}</Text>
                            </div>
                            {officerInitials && (
                                <div style={{marginTop: '10px'}}>
                                    <Text><em>{officerInitials}</em></Text>
                                </div>
                            )}
                            <div style={{marginTop: '10px'}}>
                                <Text strong>{officerName}</Text>
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Text>{officerPhone}</Text>
                            </div>
                            {/* Stamp: image if configured, otherwise SVG component */}
                            {stampImageUrl ? (
                                <img
                                    src={stampImageUrl}
                                    alt="Official Stamp"
                                    style={{
                                        marginTop: '18px',
                                        width: '140px',
                                        height: '140px',
                                        objectFit: 'contain',
                                        opacity: 0.85,
                                        transform: 'rotate(-12deg)'
                                    }}
                                />
                            ) : (
                                <StampComponent
                                    config={deductionFormConfig?.stamp}
                                    size={140}
                                    rotate={-12}
                                    style={{marginTop: '18px', opacity: 0.85}}
                                />
                            )}
                        </div>
                    </Col>
                    <Col span={12}>
                        <Text>{text.receiverHeaderText}</Text>
                        <div style={{marginTop: '20px'}}>
                            <SignatureLine/>
                            <Text>Jina...............................................</Text>
                            <div style={{marginTop: '10px'}}>
                                <Text>Cheo............................................</Text>
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Text>Saini............................................</Text>
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Text>Tarehe........................................</Text>
                            </div>
                            <div style={{marginTop: '10px'}}>
                                <Text>Mhuri...........................................</Text>
                            </div>
                        </div>
                    </Col>
                </Row>

                <ConsentBox>
                    <div style={{textAlign: 'center', marginBottom: '10px'}}>
                        <Text strong style={{textDecoration: 'underline'}}>{text.consentTitle}</Text>
                    </div>
                    <Text style={{fontSize: '11px', lineHeight: 1.5}}>
                        Mimi, <Text
                        strong>{valueData?.name || user?.fullName || '___________________'}</Text>, {text.consentIntroText}
                    </Text>
                    <ol style={{fontSize: '10px', lineHeight: '1.6', paddingLeft: '20px', margin: '10px 0 0 0'}}>
                        {(text.consentItems || []).map((item, idx) => (
                            <li key={idx}>{item.replace('{amount}', `TZS ${formattedInstallment}`).replace('{companyName}', company?.brFname || company?.brName || '')}</li>
                        ))}
                    </ol>
                    {/* Fee Breakdown — shown to member in consent section */}
                    {feeBreakdown?.hasDisbursementFees && (
                        <div style={{
                            border: '1px solid #d9d9d9',
                            padding: '10px',
                            marginTop: '12px',
                            background: '#f0f0f0'
                        }}>
                            <Text strong style={{fontSize: '11px'}}>Ada za Utoaji (Disbursement Fees):</Text>
                            {(feeBreakdown.disbursementFees || []).map((fee, idx) => (
                                <div key={idx}
                                     style={{display: 'flex', justifyContent: 'space-between', fontSize: '11px'}}>
                                    <Text>{fee.feeName}:</Text>
                                    <Text>({formatAmount(fee.calculatedAmount || 0)}) TZS</Text>
                                </div>
                            ))}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '11px',
                                borderTop: '1px solid #d9d9d9',
                                paddingTop: '4px',
                                marginTop: '4px'
                            }}>
                                <Text strong>Kiasi utakachopokea (Net Amount):</Text>
                                <Text strong>{formatAmount(feeBreakdown.netDisbursementAmount || 0)} TZS</Text>
                            </div>
                        </div>
                    )}
                    <div style={{marginTop: '24px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '6px'}}>
                            <Text strong style={{fontSize: '11px'}}>{text.memberSignatureLabel}</Text>
                            <div style={{borderBottom: '1px solid #000', width: '200px'}}/>
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <Text strong style={{fontSize: '11px'}}>{text.dateLabelInConsent}</Text>
                            <div style={{borderBottom: '1px solid #000', width: '200px'}}/>
                        </div>
                    </div>
                </ConsentBox>
            </FormContent>
        </FormContainer>
    );
};

export default WebDeductionFormComponent;
